/**
 * The orders of the selected stores, one collapsible block per status, for the
 * ABCDior and MTM platforms. Search, sort, pagination, Excel export, 30 s
 * polling and the status changes with their 6 s undo. The order details dialogs
 * are the next step of the migration (see migration.md).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faCircleNotch,
  faExclamationTriangle,
  faFileExport,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { PLATFORM, ROLES, type Platform, type Role } from '@/shared/constants';
import {
  appConfigQueryKey,
  appConfigQueryOptions,
  fetchAppConfig,
  getSelectedStoreIds,
  type StoreId,
} from '@/shared/config/app-config';
import { useQueryClient } from '@tanstack/react-query';
import { getUserProfile } from '@/features/auth/session';
import {
  infoMessageQueryOptions,
  isDisplayedOnBackOffice,
} from '@/features/info/info-message';
import { TopMenu } from '@/shared/layout/TopMenu';
import { SortableHeader } from '@/shared/ui/SortableHeader';
import { snackbar } from '@/shared/ui/snackbar';
import { updateOrder, updateOrderPart, type Order } from './api';
import { CS, ORDER_TYPES } from './order-types';
import {
  DEFAULT_PAGINATION,
  DEFAULT_SORT,
  defaultCollapsed,
  effectiveStatusTarget,
  nextOrderSort,
  orderTypesFor,
  sortedStatusIds,
  type EnrichedOrder,
  type OrderSort,
} from './logic';
import { OrdersBlock } from './OrdersBlock';
import { OrderDetailsDialog } from './OrderDetailsDialog';
import { DmsOrderDetailsDialog } from './dms/DmsOrderDetailsDialog';
import { exportOrders } from './export';
import { orderParts, partStatusChangeAllowed } from './dms/dms-logic';
import { useOrders } from './use-orders';
import { useUndoToast } from './use-undo-toast';

export const SEARCH_DEBOUNCE_MS = 500;

export interface MainPageProps {
  platform?: Platform;
}

const updatePartStatus = (
  orderId: string,
  partId: string,
  target: number,
): Promise<unknown> => {
  return updateOrderPart(orderId, {
    id: partId,
    customisationStatusId: target,
  }).catch((error) => {
    console.error(error);
  });
};

export function MainPage({
  platform = PLATFORM.ABCDIOR,
}: Readonly<MainPageProps>) {
  const { t } = useTranslation();
  const user = getUserProfile();
  const role = (user?.role ?? ROLES.SALES_ASSOCIATE) as Role;
  const queryClient = useQueryClient();
  const isDms = platform === PLATFORM.DMS;
  const orderTypes = useMemo(() => orderTypesFor(platform), [platform]);
  const statusIds = useMemo(() => sortedStatusIds(orderTypes), [orderTypes]);

  const [search, setSearch] = useState('');
  const lastSearch = useRef('');
  const [sort, setSort] = useState<OrderSort>(DEFAULT_SORT);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() =>
    defaultCollapsed(orderTypes, false),
  );
  /** Optimistic status of the orders whose change is still undoable or being saved. */
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, number>
  >({});
  const [updatingStatus, setUpdatingStatus] = useState<Record<string, boolean>>(
    {},
  );
  const [exporting, setExporting] = useState(false);
  const storeIds = useRef<readonly StoreId[]>(getSelectedStoreIds());
  const collapsedRef = useRef(collapsed);
  const sortRef = useRef(sort);
  const searchRef = useRef(search);
  useEffect(() => {
    collapsedRef.current = collapsed;
    sortRef.current = sort;
    searchRef.current = search;
  });

  const openedStatuses = useCallback(
    () => statusIds.filter((id) => !collapsedRef.current[orderTypes[id]!]),
    [statusIds, orderTypes],
  );

  const orders = useOrders({
    platform,
    enabled: !!user,
    openedStatuses,
    storeIds: () => storeIds.current,
    search: () => searchRef.current,
    sort: () => sortRef.current,
  });
  const {
    loadItems,
    setPage,
    resort,
    resetPages,
    stopPolling,
    restartPolling,
  } = orders;

  // A full load starts by recomputing which blocks are collapsed.
  const loadAll = useCallback(() => {
    const next = defaultCollapsed(orderTypes, !!searchRef.current);
    collapsedRef.current = next;
    setCollapsed(next);
    void loadItems('all');
  }, [orderTypes, loadItems]);

  // The configuration (stores) is refreshed when opening the screen.
  const isSignedIn = !!user;
  useEffect(() => {
    if (!isSignedIn) return;
    void fetchAppConfig().then(() =>
      queryClient.invalidateQueries({ queryKey: appConfigQueryKey }),
    );
    document.getElementById('search-input')?.focus();
  }, [isSignedIn, queryClient]);

  // Debounced search: a new value resets the pages and reloads everything.
  useEffect(() => {
    const handle = setTimeout(() => {
      if (search === lastSearch.current) return;
      lastSearch.current = search;
      resetPages();
      loadAll();
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search, resetPages, loadAll]);

  const info = useQuery({
    ...infoMessageQueryOptions(PLATFORM.ABCDIOR),
    enabled: !!user,
  });
  const appConfig = useQuery({ ...appConfigQueryOptions(), enabled: !!user });
  const [selectedOrder, setSelectedOrder] = useState<EnrichedOrder | null>(
    null,
  );

  /** Saves the edited order, reloads the blocks when asked. */
  const saveOrder = async (order: Order, reload: boolean) => {
    stopPolling();
    try {
      const data = await updateOrder(order);
      if (data?.error) snackbar(`Failed to update: ${data.error}`);
      if (reload) await loadItems('all');
    } catch (error) {
      console.error(error);
    } finally {
      restartPolling();
    }
  };
  const message = isDisplayedOnBackOffice(info.data) ? info.data : null;

  const onStoreSelected = (ids: StoreId[]) => {
    storeIds.current = ids;
    loadAll();
  };

  const onSortHeader = (by: string) => {
    const next = nextOrderSort(sort, by);
    setSort(next);
    sortRef.current = next;
    resort(next);
  };

  const onToggleCollapse = (statusId: number) => {
    const type = orderTypes[statusId]!;
    const next = {
      ...collapsedRef.current,
      [type]: !collapsedRef.current[type],
    };
    collapsedRef.current = next;
    setCollapsed(next);
    if (!next[type]) void loadItems(statusId);
  };

  const onPageChange = (statusId: number, page: number) => {
    const current = orders.pagination[statusId] ?? DEFAULT_PAGINATION;
    if (current.changingPage || current.page === page) return;
    setPage(statusId, page);
    // The page is read by the load from the state written just above.
    setTimeout(() => void loadItems(statusId), 0);
  };

  /** Forgets the optimistic status and the "saving" flag of an order. */
  const clearPending = (itemId: string) => {
    setUpdatingStatus((prev) => ({ ...prev, [itemId]: false }));
    setStatusOverrides((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  };

  /** Saves a validated status change, then reloads every block. */
  const saveStatus = async (
    item: EnrichedOrder,
    previousStatusId: number,
    customisationStatusId: number,
  ) => {
    stopPolling();
    try {
      const data = await updateOrder({
        ...item,
        previousStatusId,
        customisationStatusId,
      });
      if (data?.error) snackbar(`Failed to update: ${data.error}`);
      restartPolling();
      await loadItems('all');
    } catch (error) {
      console.error(error);
      restartPolling();
      snackbar('Failed to update the status, please try again');
      await loadItems('all');
    } finally {
      clearPending(item.id);
    }
  };

  /** Optimistic, undoable for 6 s, then saved. */
  const undoable = useUndoToast();
  const updateDmsPartsStatus = async (item: EnrichedOrder, target: number) => {
    stopPolling();
    setUpdatingStatus((prev) => ({ ...prev, [item.id]: true }));
    const parts = orderParts(item).filter(
      (part) =>
        part.customisationStatusId !== CS.CANCELLED &&
        partStatusChangeAllowed(item, part, target, false),
    );
    try {
      await parts.reduce<Promise<unknown>>(
        (chain, part) =>
          chain.then(() => updatePartStatus(item.id, part.id, target)),
        Promise.resolve(),
      );
    } finally {
      clearPending(item.id);
      restartPolling();
      await loadItems('all');
    }
  };

  const updateStatus = async (item: EnrichedOrder, requested: number) => {
    if (updatingStatus[item.id]) return;
    if (isDms) {
      await updateDmsPartsStatus(item, requested);
      return;
    }
    const current = item.customisationStatusId;
    const target = effectiveStatusTarget(item, requested);
    setUpdatingStatus((prev) => ({ ...prev, [item.id]: true }));
    setStatusOverrides((prev) => ({ ...prev, [item.id]: target }));
    await undoable({
      label: `${item.displayId} - Status change to ${ORDER_TYPES[target]}`,
      onCancel: () => clearPending(item.id),
      onConfirm: () => void saveStatus(item, current, target),
    });
  };

  const onPrintStickers = (item: EnrichedOrder) => {
    if (item.customisationStatusId === CS.READY_FOR_PRODUCTION)
      void updateStatus(item, CS.IN_PROGRESS);
    window.open(`/label/${item.id}`, '_blank');
  };

  const onExport = async () => {
    if (!orders.isContentLoaded || exporting) return;
    restartPolling();
    setExporting(true);
    try {
      await exportOrders(storeIds.current, platform, role);
    } catch (error) {
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  if (!user) return null;

  const sortHeader = (
    by: string,
    label: string,
    width: string,
    field: string,
    extra: CSSProperties = {},
  ) => (
    <SortableHeader
      label={label}
      width={width}
      style={extra}
      scope="col"
      active={sort.by === by}
      descending={sort.reverse}
      onClick={() => onSortHeader(by)}
      testId={`order-header-${field}`}
    />
  );

  return (
    <div className="container orders-main">
      <TopMenu platform={platform} onStoreSelected={onStoreSelected} />
      <div className="header">
        {isDms ? 'MADE TO MEASURE ORDERS' : 'ABCDIOR ORDERS'}
      </div>
      {message && (
        <div className={`message message-${message.level}`}>
          {message.level === 'green' && (
            <FontAwesomeIcon icon={faCheck} style={{ marginRight: 5 }} />
          )}
          {['red', 'orange'].includes(message.level ?? '') && (
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              style={{ marginRight: 5 }}
            />
          )}{' '}
          {message.message}
        </div>
      )}
      {orders.isLoadingItems && (
        <FontAwesomeIcon
          icon={faCircleNotch}
          spin
          className="loading-icon"
          aria-label="Loading"
        />
      )}
      <div className="order-table mx-auto">
        <div className="theader mb-2 p-2">
          <table
            className="table table-borderless"
            style={{ tableLayout: 'fixed' }}
          >
            <thead>
              <tr>
                {!isDms && (
                  <>
                    {sortHeader('createdAt', t('orders.id'), '6%', 'id', {
                      maxWidth: 72,
                    })}
                    {role !== ROLES.WORKSHOP &&
                      sortHeader(
                        'lastName',
                        t('orders.lastName'),
                        '5%',
                        'last-name',
                      )}
                    {role !== ROLES.WORKSHOP &&
                      sortHeader(
                        'firstName',
                        t('orders.firstName'),
                        '6%',
                        'first-name',
                      )}
                    {role !== ROLES.WORKSHOP &&
                      sortHeader(
                        'sellerName',
                        t('orders.sellerName'),
                        '5%',
                        'seller',
                      )}
                    {sortHeader('productName', t('orders.item'), '7%', 'item')}
                    {role === ROLES.WORKSHOP &&
                      sortHeader(
                        'productReference',
                        t('orders.productReference'),
                        '6%',
                        'product-reference',
                      )}
                    {sortHeader('customText', t('orders.text'), '5%', 'text')}
                    {role === ROLES.WORKSHOP &&
                      sortHeader(
                        'color.name',
                        t('orders.productColor'),
                        '5%',
                        'color',
                      )}
                    {role === ROLES.WORKSHOP &&
                      sortHeader('oaCode', 'OA', '4%', 'oa')}
                    {sortHeader(
                      'workshopName',
                      t('orders.workshop'),
                      '6%',
                      'workshop',
                    )}
                    <th style={{ width: '6%' }} scope="col">
                      {t('orders.status')}
                    </th>
                  </>
                )}
                {isDms && (
                  <>
                    {sortHeader('createdAt', t('orders.order'), '15%', 'id', {
                      maxWidth: 72,
                    })}
                    {sortHeader(
                      'lastName',
                      t('orders.lastName'),
                      '8%',
                      'last-name',
                    )}
                    {sortHeader(
                      'firstName',
                      t('orders.firstName'),
                      '8%',
                      'first-name',
                    )}
                    <th style={{ width: '5%' }} scope="col">
                      {t('orders.shirts')}
                    </th>
                    <th style={{ width: '5%' }} scope="col">
                      {t('orders.others')}
                    </th>
                    <th style={{ width: '7%' }} scope="col">
                      {t('orders.totalItems')}
                    </th>
                    <th style={{ width: '5%' }} scope="col">
                      {t('orders.express')}
                    </th>
                    <th style={{ width: '13%' }} scope="col">
                      {t('orders.deliveryDateInStore')}
                    </th>
                    <th style={{ width: '6%' }} scope="col">
                      {t('orders.status')}
                    </th>
                  </>
                )}
                <th
                  style={{ width: '15%' }}
                  scope="col"
                  className="search-bar-border"
                >
                  <div className="input-group">
                    <input
                      className="form-control border-0 bg-transparent py-2"
                      id="search-input"
                      data-testid="order-search-input"
                      style={{ boxShadow: 'none' }}
                      type="search"
                      placeholder={t('orders.searchForAnOrders')}
                      aria-label={t('orders.searchForAnOrders')}
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                    <span className="input-group-append border-0">
                      <div className="input-group-text border-0 bg-transparent">
                        <FontAwesomeIcon icon={faSearch} />
                      </div>
                    </span>
                  </div>
                </th>
                {!isDms && (
                  <th
                    style={{ width: '3%' }}
                    scope="col"
                    className="search-bar-border pointer-cursor"
                  >
                    <button
                      type="button"
                      className="export-button"
                      aria-label="Export to Excel"
                      data-testid="order-download-button"
                      onClick={() => void onExport()}
                    >
                      <FontAwesomeIcon
                        icon={
                          orders.isContentLoaded && !exporting
                            ? faFileExport
                            : faCircleNotch
                        }
                        spin={!(orders.isContentLoaded && !exporting)}
                      />
                    </button>
                  </th>
                )}
              </tr>
            </thead>
          </table>
        </div>
        {orders.isContentLoaded && (
          <div>
            {statusIds.map((id) => (
              <OrdersBlock
                key={id}
                platform={platform}
                role={role}
                statusId={id}
                items={(orders.customizations[id] ?? []).map((item) =>
                  statusOverrides[item.id] !== undefined
                    ? {
                        ...item,
                        customisationStatusId: statusOverrides[item.id]!,
                      }
                    : item,
                )}
                pagination={orders.pagination[id] ?? DEFAULT_PAGINATION}
                collapsed={!!collapsed[orderTypes[id]!]}
                updatingStatus={updatingStatus}
                onToggleCollapse={() => onToggleCollapse(id)}
                onPageChange={(page) => onPageChange(id, page)}
                onUpdateStatus={(item, target) =>
                  void updateStatus(item, target)
                }
                onOpenDetails={setSelectedOrder}
                onPrintStickers={onPrintStickers}
              />
            ))}
          </div>
        )}
      </div>
      {selectedOrder && !isDms && (
        <OrderDetailsDialog
          key={selectedOrder.id}
          order={selectedOrder}
          role={role}
          productLines={appConfig.data?.productLines}
          onClose={() => setSelectedOrder(null)}
          onSave={(order, reload) => void saveOrder(order, reload)}
        />
      )}
      {selectedOrder && isDms && (
        <DmsOrderDetailsDialog
          key={selectedOrder.id}
          order={selectedOrder}
          role={role}
          userId={user.id}
          onClose={() => setSelectedOrder(null)}
          onSave={(order, reload) => void saveOrder(order, reload)}
          onPollingChange={(running) =>
            running ? restartPolling() : stopPolling()
          }
          onReload={() => void loadItems('all')}
        />
      )}
    </div>
  );
}
